import { FC } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { FaApple, FaAndroid, FaLink } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useModal } from 'store/modal';
import styled from 'styled-components';

const InstructionsEN: FC = () => {
  const { showModal } = useModal();

  const handleAlternativeMethodClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    showModal({
      type: 'info',
      heading: 'Алтернативен метод',
      size: 'lg',
      content: (
        <div className="d-flex flex-column text-left px-3">
          <h5>If you have trouble scanning the QR code with your camera, you can enter it manually.</h5>
          <ol>
            <li>Press the +(plus) button.</li>
            <li>
              Select <b>“Other Account“</b>
            </li>
            <li>Select the option available at the bottom “Or enter code manually“</li>
            <li>
              In the <b>“Account name“</b> field, enter - eHR and your email
            </li>
            <li>
              In the <b>“Secret key“</b> field, enter the Key value from the page below, for example{' '}
              <b>“ABC12345612345FF“</b>
            </li>
            <li>
              Press <b>“Finish“</b>
            </li>
            <li>
              In eHR, press the green <b>“Activate“</b> button
            </li>
          </ol>
        </div>
      ),
    });
  };

  return (
    <>
      <p>
        In order to use the system, it is necessary to activate the “Two Factor Authentication“ function. To do this,
        follow the steps below:
      </p>
      <Tabs defaultActiveKey="android">
        <Tab
          eventKey="android"
          title={
            <>
              <FaAndroid
                size={20}
                style={{ marginRight: '0.25rem' }}
              />{' '}
              Android
            </>
          }
        >
          <div className="pt-3 pb-3">
            <StyledOl>
              <StyledLi>
                Look for the <b>“Microsoft Authenticator“</b> app on your phone or tablet, open it, and proceed to{' '}
                <b>step 4 below</b>.
              </StyledLi>
              <StyledLi>
                If you do not have it installed, you can download and install it from{' '}
                <Link
                  to={{
                    pathname: 'https://play.google.com/store/apps/details?id=com.azure.authenticator',
                  }}
                  target="_blank"
                >
                  <FaLink size={12} /> HERE
                </Link>{' '}
                or from the <b>Play Store</b>
              </StyledLi>
              <StyledLi>
                If you&apos;ve just installed it, upon starting:
                <NestedOl>
                  <NestedLi>
                    Press <b>“Accept“</b>, then <b>“Continue“</b>.
                  </NestedLi>
                  <NestedLi>The app may ask for access to your camera, please allow it.</NestedLi>
                  <NestedLi>
                    Point your phone towards the <b>QR code</b> below.
                  </NestedLi>
                  <NestedLi>
                    Proceed to <b>step 5</b>.
                  </NestedLi>
                </NestedOl>
              </StyledLi>
              <StyledLi>
                If you already have the app, in the upper right corner, press the +(plus) button.
                <NestedOl>
                  <NestedLi>
                    Select the last option <b>“Other Account“</b>
                  </NestedLi>
                  <NestedLi>The app may ask for access to your camera, please allow it.</NestedLi>
                  <NestedLi>Point your phone towards the QR code below.</NestedLi>
                  <NestedLi>
                    If you encounter difficulties using the camera, you can use{' '}
                    <div
                      style={{
                        color: '#716aca',
                        cursor: 'pointer',
                        textDecoration: 'none',
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                      onFocus={e => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onBlur={e => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                      onClick={handleAlternativeMethodClick}
                    >
                      <FaLink size={12} /> Alternative Method
                    </div>
                  </NestedLi>
                </NestedOl>
              </StyledLi>
              <StyledLi>
                The code from the app will appear on your screen, and it is necessary to enter it in the <b>“Code“</b>{' '}
                field in eHR. The code changes every 30 seconds.
              </StyledLi>
              <StyledLi>
                Press the green <b>“Activate“</b> button.
              </StyledLi>
              <StyledLi>
                <b>If the code is correct, you will activate two-factor authentication - congratulations!</b>
              </StyledLi>
              <StyledLi>
                If you fail, wait for a new code to be generated and try again. Alternatively, you can refresh the page
                and attempt to scan it again.
              </StyledLi>
              <StyledLi>
                For the two-factor authentication to work correctly, the date and time on your phone must be accurate.
                Please check this in the Settings menu.
              </StyledLi>
            </StyledOl>
          </div>
        </Tab>
        <Tab
          eventKey="ios"
          title={
            <>
              <FaApple
                size={20}
                style={{ marginRight: '0.25rem' }}
              />{' '}
              iOS
            </>
          }
        >
          <div className="pt-3 pb-3">
            <StyledOl>
              <StyledLi>
                Look for the <b>“Microsoft Authenticator“</b> app on your phone or tablet, open it, and proceed to{' '}
                <b>step 4 below</b>.
              </StyledLi>
              <StyledLi>
                If you do not have it installed, you can download and install it from{' '}
                <Link
                  to={{
                    pathname: 'https://apps.apple.com/us/app/microsoft-authenticator/id983156458',
                  }}
                  target="_blank"
                >
                  <FaLink size={12} /> HERE
                </Link>{' '}
                or from the <b>App Store</b>
              </StyledLi>
              <StyledLi>
                If you&apos;ve just installed it, upon starting:
                <NestedOl>
                  <NestedLi>
                    Press <b>“Accept“</b>, then <b>“Continue“</b>.
                  </NestedLi>
                  <NestedLi>The app may ask for access to your camera, please allow it.</NestedLi>
                  <NestedLi>
                    Point your phone towards the <b>QR code</b> below.
                  </NestedLi>
                  <NestedLi>
                    Proceed to <b>step 5</b>.
                  </NestedLi>
                </NestedOl>
              </StyledLi>
              <StyledLi>
                If you already have the app, in the upper right corner, press the +(plus) button.
                <NestedOl>
                  <NestedLi>
                    Select the last option <b>“Other Account“</b>
                  </NestedLi>
                  <NestedLi>The app may ask for access to your camera, please allow it.</NestedLi>
                  <NestedLi>Point your phone towards the QR code below.</NestedLi>
                  <NestedLi>
                    If you encounter difficulties using the camera, you can use{' '}
                    <div
                      style={{
                        color: '#716aca',
                        cursor: 'pointer',
                        textDecoration: 'none',
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                      onFocus={e => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onBlur={e => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                      onClick={handleAlternativeMethodClick}
                    >
                      <FaLink size={12} /> Alternative Method
                    </div>
                  </NestedLi>
                </NestedOl>
              </StyledLi>
              <StyledLi>
                The code from the app will appear on your screen, and it is necessary to enter it in the <b>“Code“</b>{' '}
                field in eHR. The code changes every 30 seconds.
              </StyledLi>
              <StyledLi>
                Press the green <b>“Activate“</b> button.
              </StyledLi>
              <StyledLi>
                <b>If the code is correct, you will activate two-factor authentication - congratulations!</b>
              </StyledLi>
              <StyledLi>
                If you fail, wait for a new code to be generated and try again. Alternatively, you can refresh the page
                and attempt to scan it again.
              </StyledLi>
              <StyledLi>
                For the two-factor authentication to work correctly, the date and time on your phone must be accurate.
                Please check this in the Settings menu.
              </StyledLi>
            </StyledOl>
          </div>
        </Tab>
      </Tabs>
    </>
  );
};

export default InstructionsEN;

// Styled wrapper for the main ordered list
const StyledOl = styled.ol`
  counter-reset: section;
  list-style-type: none;
  padding-left: 20px;
`;

// Styled component for top-level list items
const StyledLi = styled.li`
  counter-increment: section;
  &:before {
    content: counters(section, '.') '. ';
    margin-right: 5px;
  }
`;

// Styled wrapper for nested ordered lists
const NestedOl = styled.ol`
  counter-reset: subsection;
  list-style-type: none;
  padding-left: 40px; // Indent nested list items
`;

// Styled component for nested list items
const NestedLi = styled.li`
  counter-increment: subsection;
  &:before {
    content: counter(section) '.' counter(subsection) ' ';
    margin-right: 5px;
  }
`;
